import type { BaseFolderSecurity } from '@scio3/shared-types';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * FROZEN LEGACY — production's FRONTEND folder evaluator (#2452, seam S5).
 *
 * The two helpers below are VERBATIM COPIES of `origin/main @ 912267ce3`
 * `apps/web/src/hooks/usePermissions.ts` — `checkSecurities` (:343) and `checkSecurityTeams` (:382).
 * They serve the OFF release cohort: every account created before Group Security.
 *
 * This is the FRONTEND MIRROR of `__peFolderSecurity` / `updateTeamIdFilterFolderSecurity` on the
 * backend. The two must agree per cohort, because when they disagree the user gets the "button is
 * shown, mutation is refused" class of bug — or worse, the silent inverse.
 *
 * ⛔ DO NOT FEATURE-EDIT. A Group Security change belongs in `resolveFolderAccess`
 * (`usePermissions.ts`). A change here changes what existing customers already have in production.
 * Legitimate edits: a genuine production bug fix (which must also land on `main`), and compile
 * adaptations, which are listed below.
 *
 * Adaptations vs main, in full:
 *   1. The two helpers are renamed with a `…FolderSecurity` suffix and moved to this file, so the
 *      live `usePermissions.ts` cannot shadow them or pick them up by autocomplete.
 *   2. `resolveFolderAccessFolderSecurity` is NEW — it is the composition main performs INLINE in
 *      `getPermissions.checkFolderAccess` (:279-284), lifted into a function so the Group Security
 *      dispatcher can call it with the same signature shape its own evaluator uses. The logic is
 *      main's, moved not rewritten; see the mapping note on that function.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** Result shape shared with the Group Security evaluator, so the dispatcher's arms are swappable. */
export type FolderAccessVerdict = { canView: boolean; canEdit: boolean };

/**
 * FROZEN — main `usePermissions.ts:343`.
 * Check if user is in the securities[] array for same-team folder access.
 */
function checkSecuritiesFolderSecurity(
  folder: BaseFolderSecurity,
  userId: string | undefined,
  isAdmin: boolean,
): boolean {
  const securities = folder.securities;

  // Empty array or null = EVERYONE_TEAM (all team members can access)
  if (!securities || securities.length === 0) {
    return true;
  }

  // Check special userId values
  for (const entry of securities) {
    // "-2" = EVERYONE_ACCOUNT (all users in account)
    if (entry.userId === '-2') return true;

    // "0" = EVERYONE_TEAM (all team members)
    if (entry.userId === '0') return true;

    // "-1" = ONLY_ADMIN baseline. Admins always pass. A non-admin must NOT be rejected
    // here — they may still be listed explicitly elsewhere in the array (#1452: "Admin Only"
    // coexists with individual users, who bypass the admin-only gate). So don't short-circuit
    // on the sentinel; keep scanning for an explicit userId match.
    if (entry.userId === '-1') {
      if (isAdmin) return true;
      continue;
    }

    // Check specific user ID
    if (entry.userId === userId) return true;
  }

  return false;
}

/**
 * FROZEN — main `usePermissions.ts:382`.
 * Check if current team is in securityTeams[] for cross-team folder access.
 */
function checkSecurityTeamsFolderSecurity(folder: BaseFolderSecurity, teamId: string): boolean {
  const securityTeams = folder.securityTeams;

  // If no securityTeams, folder is not shared cross-team
  if (!securityTeams || securityTeams.length === 0) {
    return false;
  }

  // Check if current team is in the list
  if (securityTeams.includes(teamId)) {
    return true;
  }

  // Check for "-2" (EVERYONE_ACCOUNT - all teams in account)
  if (securityTeams.includes('-2')) {
    return true;
  }

  return false;
}

/**
 * Production's folder verdict for ONE folder — adaptation 2 above.
 *
 * Main decides this inline inside `checkFolderAccess`, in three steps that this function performs
 * in the same order:
 *   1. `if (context.folder?.isFolderSecurityEdit === false) return false` — the folder-wide EDIT
 *      flag (main :268). In main that denies the whole "can act" answer; here it lands on `canEdit`,
 *      which is the branch's name for the same thing. `canView` is unaffected, matching main, where
 *      a read-only folder is still listed.
 *   2. cross-teamspace ⇒ `checkSecurityTeams(folder, activeTeamId)` (main :280).
 *   3. same teamspace ⇒ `checkSecurities(folder, userId, isAdminOnTeam)` (main :284).
 *
 * The caller still owns everything main checks BEFORE this point — super-admin bypass, system
 * folders, the same-team admin bypass, and the `roleSecurity` / `folderLevelSecurity` master
 * gates — so none of it is duplicated here.
 *
 * ⚠ CORRECTED 2026-10-18 (scan finding 2). An earlier version of this note said the master gates
 * run BEFORE the `isFolderSecurityEdit === false` denial. They do not. On `origin/main` the two
 * denials sit ABOVE both short-circuits, so a folder stored read-only stays read-only even when
 * `folderLevelSecurity` is false — which is exactly the state a plan DOWNGRADE leaves behind
 * (`featureData.folderLevelSecurityLocked`). The wrong note was the written justification for
 * hoisting the gates in `usePermissions.checkFolderAccess`, and that hoist handed edit affordances
 * to standard-role users on folders production renders view-only. The caller now performs the OFF
 * cohort's flag check before its master gates; this sibling keeps owning the `canEdit` half.
 *
 * ⚠ Reads `securityTeams`, which v4 had dropped from `BaseFolderSecurity` AND from all 23 folder
 * selections in the GraphQL operations. Both are restored; without them this returns `false` for
 * every cross-teamspace folder and the failure is silent.
 */
export function resolveFolderAccessFolderSecurity(
  folder: BaseFolderSecurity,
  userId: string | undefined,
  activeTeamId: string,
  isAdminOnTeam: boolean,
): FolderAccessVerdict {
  const isCrossTeamFolder = folder.teamId != null && folder.teamId !== activeTeamId;
  const access = isCrossTeamFolder
    ? checkSecurityTeamsFolderSecurity(folder, activeTeamId)
    : checkSecuritiesFolderSecurity(folder, userId, isAdminOnTeam);

  return {
    canView: access,
    canEdit: access && folder.isFolderSecurityEdit !== false,
  };
}
